import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Select,
  Empty,
  Typography,
  Alert,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useSearchParams } from "react-router-dom";
import apiHelpers from "./data";
import buildColumns from "./columns";
import VideoFormModal from "./VideoFormModal";
import VideoDetailDrawer, { KpDrawer } from "./VideoDetailDrawer";
import HardDeleteModal from "./HardDeleteModal";
import { STORES, hashForLabel, labelForHash } from "../BlogsTable/stores";
import { triggerNotification } from "../../common/notify";
import { DEFAULT_STORE_LABEL, SHARED_STORE_LS_KEY } from "../common/storeDefaults";

const LS_STORE_KEY = SHARED_STORE_LS_KEY;

const STORE_OPTIONS = [
  ...STORES.map((s) => ({ value: s.hash, label: `${s.label} — ${s.name}` })),
];

const DEFAULT_OPTIONS = [
  { value: "", label: "Any" },
  { value: 1, label: "Default only" },
  { value: 0, label: "Non-default only" },
];

const KEYWORD_FILTER_OPTIONS = [
  { value: "all",           label: "All Videos" },
  { value: "with_tags",    label: "Tagged Videos" },
  { value: "without_tags", label: "Untagged Videos" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const validStoreHashes = STORES.map((s) => s.hash);

const parse01 = (v) => (v === "0" ? 0 : v === "1" ? 1 : "");

const VALID_KEYWORD_FILTERS = new Set(KEYWORD_FILTER_OPTIONS.map((o) => o.value));

const readInitialFilters = (searchParams) => {
  const lsStoreLabel = localStorage.getItem(LS_STORE_KEY);
  const lsStoreHash = hashForLabel(lsStoreLabel);
  const qpStore = searchParams.get("vstore");
  let store_hash;
  if (qpStore === "" || qpStore === null) {
    store_hash = lsStoreHash || hashForLabel(DEFAULT_STORE_LABEL) || "";
  } else {
    store_hash = validStoreHashes.includes(qpStore) ? qpStore : "";
  }

  const rawKwf = searchParams.get("vkwf");

  return {
    store_hash,
    search:          searchParams.get("vsearch") || "",
    keyword:         searchParams.get("vkw") || "",
    keyword_filter:  VALID_KEYWORD_FILTERS.has(rawKwf) ? rawKwf : "all",
    is_default: parse01(searchParams.get("vdefault")),
    page: Math.max(parseInt(searchParams.get("vpage"), 10) || 1, 1),
    limit: PAGE_SIZE_OPTIONS.includes(parseInt(searchParams.get("vlimit"), 10))
      ? parseInt(searchParams.get("vlimit"), 10)
      : 20,
  };
};

const VideosTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState(() => readInitialFilters(searchParams));
  const [searchInput, setSearchInput] = useState(filters.search);
  const [keywordInput, setKeywordInput] = useState(filters.keyword);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [formState, setFormState] = useState({ open: false, mode: "create", id: null });
  const [detailId, setDetailId] = useState(null);
  const [hardDeleteRecord, setHardDeleteRecord] = useState(null);
  const [kpRecord, setKpRecord] = useState(null);
  const [tagCountMap, setTagCountMap] = useState({});

  useEffect(() => {
    const label = labelForHash(filters.store_hash);
    if (label) localStorage.setItem(LS_STORE_KEY, label);
  }, [filters.store_hash]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("vstore", filters.store_hash);
    if (filters.search) next.set("vsearch", filters.search);
    else next.delete("vsearch");
    if (filters.keyword) next.set("vkw", filters.keyword);
    else next.delete("vkw");
    if (filters.keyword_filter && filters.keyword_filter !== "all")
      next.set("vkwf", filters.keyword_filter);
    else next.delete("vkwf");
    if (filters.is_default === 0 || filters.is_default === 1)
      next.set("vdefault", String(filters.is_default));
    else next.delete("vdefault");
    next.set("vpage", String(filters.page));
    next.set("vlimit", String(filters.limit));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.search === searchInput ? prev : { ...prev, search: searchInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((prev) =>
        prev.keyword === keywordInput ? prev : { ...prev, keyword: keywordInput, page: 1 }
      );
    }, 300);
    return () => clearTimeout(id);
  }, [keywordInput]);

  const fetchData = useCallback(async () => {
    setFetching(true);
    setLoadError(null);
    try {
      const { items, pagination: pg } = await apiHelpers.fetchVideos(filters);
      setRows(items);
      setPagination(pg);
    } catch (err) {
      const mapped = apiHelpers.mapError(err);
      setLoadError(mapped);
      setRows([]);
      setPagination({ total: 0, total_pages: 0 });
      triggerNotification({
        type: "error",
        title: "Failed to load videos",
        message: mapped.message,
      });
    } finally {
      setLoading(false);
      setFetching(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateFilter = (patch) =>
    setFilters((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));

  const resetFilters = () => {
    setSearchInput("");
    setKeywordInput("");
    setFilters({
      store_hash: hashForLabel(DEFAULT_STORE_LABEL) || "",
      search:     "",
      keyword:    "",
      keyword_filter: "all",
      is_default: "",
      page:       1,
      limit:      20,
    });
  };

  const openCreate = () => setFormState({ open: true, mode: "create", id: null });
  const openEdit = useCallback(
    (record) => setFormState({ open: true, mode: "edit", id: record.id }),
    []
  );
  const closeForm = () => setFormState((s) => ({ ...s, open: false }));

  const handleView = useCallback((record) => setDetailId(record.id), []);

  const handleHardDelete = useCallback((record) => {
    setHardDeleteRecord(record);
  }, []);

  const handleManageTags = useCallback((record) => {
    setKpRecord(record);
  }, []);

  const columns = useMemo(
    () =>
      buildColumns({
        onView: handleView,
        onEdit: openEdit,
        onManageTags: handleManageTags,
        onHardDelete: handleHardDelete,
        page: filters.page,
        limit: filters.limit,
        tagCountMap,
      }),
    [handleView, openEdit, handleManageTags, handleHardDelete, filters.page, filters.limit, tagCountMap]
  );

  const showFullPageLoader = loading && !rows.length;

  return (
    <>
      <div style={{ marginBottom: 12 }} className="TableFilterBox">
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Space wrap>
            <Select
              value={filters.store_hash}
              onChange={(v) => updateFilter({ store_hash: v })}
              options={STORE_OPTIONS}
              style={{ width: 220 }}
              placeholder="Store"
            />
            <Input
              allowClear
              placeholder="Search title / ID / desc"
              prefix={<SearchOutlined />}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              style={{ width: 230 }}
            />
            <Input
              allowClear
              placeholder="Has tag"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              style={{ width: 150 }}
            />
            <Select
              value={filters.keyword_filter}
              onChange={(v) => updateFilter({ keyword_filter: v })}
              options={KEYWORD_FILTER_OPTIONS}
              style={{ width: 170 }}
            />
            <Select
              value={filters.is_default}
              onChange={(v) => updateFilter({ is_default: v })}
              options={DEFAULT_OPTIONS}
              style={{ width: 160 }}
            />
            <Select
              value={filters.limit}
              onChange={(v) => updateFilter({ limit: v })}
              options={PAGE_SIZE_OPTIONS.map((n) => ({ value: n, label: `${n} / page` }))}
              style={{ width: 120 }}
            />
            <Button icon={<ReloadOutlined />} onClick={resetFilters}>
              Reset
            </Button>
          </Space>
          <Space>
<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Video
            </Button>
          </Space>
        </Space>
      </div>

      {loadError && !fetching && (
        <Alert
          type="error"
          showIcon
          message={loadError.message}
          action={
            <Button size="small" onClick={fetchData}>
              Retry
            </Button>
          }
          style={{ marginBottom: 12 }}
        />
      )}

      <div
        className="TableBox"
        style={{
          maxHeight: 700,
          overflow: "auto",
          opacity: fetching && !showFullPageLoader ? 0.6 : 1,
          transition: "opacity 0.15s",
        }}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={rows}
          loading={showFullPageLoader}
          rowClassName={(record) =>
            Number(record.isDefault) === 1 ? "row-default" : ""
          }
          onRow={(record) => ({
            onClick: () => handleView(record),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <Empty
                description={
                  <span>
                    No videos found. Try adjusting filters or click <b>New Video</b>.
                  </span>
                }
              />
            ),
          }}
          pagination={{
            current: filters.page,
            pageSize: filters.limit,
            total: pagination.total,
            showSizeChanger: false,
            showQuickJumper: true,
            position: ["bottomLeft"],
            showTotal: (total) => (
              <Typography.Text type="secondary">
                {total} result{total === 1 ? "" : "s"} · Page {filters.page} of{" "}
                {pagination.total_pages || 1}
              </Typography.Text>
            ),
            onChange: (page) => setFilters((prev) => ({ ...prev, page })),
          }}
          bordered
          scroll={{ x: 1150, y: 500 }}
        />
      </div>

      <VideoFormModal
        open={formState.open}
        mode={formState.mode}
        videoId={formState.id}
        defaultStoreHash={filters.store_hash || hashForLabel(DEFAULT_STORE_LABEL)}
        defaultStoreLabel=""
        onClose={closeForm}
        onSaved={() => {
          closeForm();
          fetchData();
        }}
      />

      <VideoDetailDrawer
        open={!!detailId}
        videoId={detailId}
        onClose={() => setDetailId(null)}
        onChanged={fetchData}
        onEdit={(id) => {
          setDetailId(null);
          setFormState({ open: true, mode: "edit", id });
        }}
      />

      <HardDeleteModal
        open={!!hardDeleteRecord}
        record={hardDeleteRecord}
        onCancel={() => setHardDeleteRecord(null)}
        onConfirmed={() => {
          setHardDeleteRecord(null);
          fetchData();
        }}
      />

      <KpDrawer
        open={!!kpRecord}
        video={kpRecord}
        onClose={() => setKpRecord(null)}
        onChanged={fetchData}
        onCountChange={(videoId, count) =>
          setTagCountMap((prev) =>
            prev[videoId] === count ? prev : { ...prev, [videoId]: count }
          )
        }
      />
    </>
  );
};

export default VideosTable;
